import React, { useState, useRef, useEffect } from 'react';
import { Award } from 'lucide-react';
import '../../../styles/resume-editor.css';

interface Certification {
  uid: string;
  certification_name: string;
  organization_name?: string;
  issued_at?: string | null;
  expires_at?: string | null;
  credential_id?: string;
  description?: string;
}

interface CertificationCardProps {
  certification: Certification;
  onEdit: (certification: Certification) => void;
  onDelete: (uid: string) => void;
}

export const CertificationCard: React.FC<CertificationCardProps> = ({ certification, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const formatDateRange = (): string => {
    const issuedDate = formatDate(certification.issued_at);
    const expiresDate = certification.expires_at ? formatDate(certification.expires_at) : null;

    if (issuedDate && expiresDate) {
      return `${issuedDate} - ${expiresDate}`;
    }
    if (issuedDate) {
      return `Issued: ${issuedDate}`;
    }
    return '';
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="resume-editor__experience-card">
      <div className="resume-editor__experience-card-header">
        <div className="resume-editor__experience-card-info">
          <h3 className="resume-editor__experience-card-title">{certification.certification_name}</h3>
          <div className="resume-editor__experience-card-company-row">
            <div className="resume-editor__experience-card-company">
              <div className="resume-editor__experience-card-logo">
                <Award size={16} />
              </div>
              <p className="resume-editor__experience-card-subtitle">{certification.organization_name || ''}</p>
            </div>
          </div>
        </div>
        <div className="resume-editor__experience-card-menu-container" ref={menuRef}>
          <button 
            className="resume-editor__experience-card-menu" 
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {certification.issued_at && (
            <p className="resume-editor__experience-card-date">{formatDateRange()}</p>
          )}
          {menuOpen && (
            <div className="resume-editor__experience-card-dropdown">
              <button
                className="resume-editor__experience-card-dropdown-item"
                onClick={() => {
                  onEdit(certification);
                  setMenuOpen(false);
                }}
              >
                Edit certification
              </button>
              <button
                className="resume-editor__experience-card-dropdown-item resume-editor__experience-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(certification.uid);
                  setMenuOpen(false);
                }}
              >
                Delete certification
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="resume-editor__experience-card-body">
        {certification.credential_id && (
          <p className="resume-editor__experience-card-description">
            <strong>Credential ID:</strong> {certification.credential_id}
          </p>
        )}
        {certification.description && (
          <p className="resume-editor__experience-card-description">{certification.description}</p>
        )}
      </div>
    </div>
  );
};

